import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  DocumentStatus,
  DraftInterfaceSliceState,
  PostDraftInterface,
} from '../interfaces/post.interface';
import type { TextBlock } from '../interfaces/textEditor.interface';

const textBlocksInitialState: TextBlock = {
  id: '',
  type: undefined,
  data: {
    paragraph: '',
    header: '',
    level: 0,
    items: [],
    file: undefined,
    code: '',
    withBorder: false,
    withBackground: false,
    stretched: false,
    caption: '',
    style: 'unordered',
  },
};

const draftInitialState: PostDraftInterface = {
  hasStoryAnalysis: false,
  storyAnalysis: [],
  id: '',
  parentPostId: '',
  status: DocumentStatus.DRAFT,
  categories: [],
  textBlocks: [],
  article: true,
  textBlocksVersions: [],
  username: '',
  blocks: [],
};

const draftStoreInitialState: DraftInterfaceSliceState = {
  drafts: [],
  draft: draftInitialState,
  categories: [],
  addDraft: false,
  textBlocks: [textBlocksInitialState],
  createDraft: false,
  editDraft: false,
  isEditingDraft: false,
  isAddingDraft: false,
  isAddingPostReplyAsDraft: false,
  saveDraftStart: false,
  saveDraftSuccess: false,
  refreshDraft: false,
  publishDraft: false,
  publishDraftInit: false,
  publishDraftSuccess: false,
  updatedPostDraft: {},
  deletedDraftId: '',
  savedImageKey: [],
  reAnalyzeStory: false,
  analyzedSnapshot: null,
  feedPostId: undefined,
  lastPublishedTopic: null,
};

export const draftSlice = createSlice({
  name: 'draftStore',
  initialState: draftStoreInitialState,
  reducers: {
    GET_DRAFT_STORE_ACTION: (state, action: PayloadAction<any>) => {
      state.draft = action.payload;
    },
    ADD_DRAFT_STORE_ACTION: (state, action: PayloadAction<any>) => {
      state.draft = action.payload;
    },
    IS_ADDING_DRAFT_STORE_ACTION: (state, action: PayloadAction<boolean>) => {
      state.isAddingDraft = action.payload;
    },
    IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isAddingPostReplyAsDraft = action.payload;
    },
    FEED_POST_ID: (state, action: PayloadAction<string | undefined>) => {
      state.feedPostId = action.payload;
    },
    SAVE_POST_DRAFT: (state, action: PayloadAction<boolean>) => {
      // Before a post get's published it is saved in draft mode.
      state.createDraft = action.payload;
    },
    SAVE_DRAFT_START_STORE_ACTION: (state, action: PayloadAction<boolean>) => {
      state.saveDraftStart = action.payload;
    },
    SAVE_DRAFT_SUCCESS_STORE_ACTION: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.saveDraftSuccess = action.payload;
    },
    EDITED_DRAFT_STORE_ACTION: (
      state,
      action: PayloadAction<PostDraftInterface>,
    ) => {
      state.updatedPostDraft = action.payload;
    },
    REFRESH_DRAFT_STORE_ACTION: (state, action: PayloadAction<any>) => {
      state.refreshDraft = action.payload;
    },
    EDIT_POST_DRAFT: (state) => {
      state.editDraft = true;
    },
    IS_EDITING_DRAFT_STORE_ACTION: (state, action: PayloadAction<boolean>) => {
      state.isEditingDraft = action.payload;
    },
    ADD_TEXT_BLOCKS: (state, action: PayloadAction<TextBlock[]>) => {
      state.textBlocks = action.payload;
    },
    RESET_DRAFT_STATE: (state, action: PayloadAction<boolean>) => {
      state.draft = draftInitialState;
    },
    RESET_POST_REPLY_DRAFT_STATE: (state, action: PayloadAction<boolean>) => {
      state.draft = draftInitialState;
    },
    SAVE_DRAFT_CATEGORIES: (state, action: PayloadAction<string[]>) => {
      state.draft.categories = action.payload;
    },
    RESET_DRAFT_CATEGORIES: (state) => {
      (state.categories as string[]) = [];
    },
    RESET_UPDATED_DRAFT: (state) => {
      state.updatedPostDraft = {};
    },
    POPULATE_CATEGORIES: (state, action: PayloadAction<string[]>) => {
      (state.categories as string[]) = action.payload;
    },
    PUBLISH_DRAFT_INIT: (state, action: PayloadAction<boolean>) => {
      state.publishDraftInit = action.payload;
    },
    PUBLISH_DRAFT_SUCCESS: (state, action: PayloadAction<boolean>) => {
      state.publishDraftSuccess = action.payload;
    },
    // TRIL-116: signal that drives App.tsx's <PostPublishPrompt /> mount.
    SET_LAST_PUBLISHED_TOPIC: (state, action: PayloadAction<string | null>) => {
      state.lastPublishedTopic = action.payload;
    },
    SET_EDITED_DRAFT: (state, action: PayloadAction<PostDraftInterface>) => {
      state.updatedPostDraft = action.payload;
    },
    SAVED_IMAGE_KEY: (state, action: PayloadAction<string>) => {
      // @ts-ignore
      state.savedImageKey.push(action.payload);
    },
    CLEAR_IMAGE_KEY: (state, action: PayloadAction<boolean>) => {
      // @ts-ignore
      state.savedImageKey = [];
    },
    RESET_IMAGE_KEYS: (state, action: PayloadAction<string[]>) => {
      state.savedImageKey = [];
      // @ts-ignore
      state.savedImageKey = action.payload;
    },
    SET_RE_ANALYZE_ACTION: (state, action: PayloadAction<boolean>) => {
      state.reAnalyzeStory = action.payload;
    },
    // TRIL-313: capture the text that was just analyzed (raw; the
    // normalized-diff is computed at the edit trigger). null = no current
    // analysis (e.g. after "Revise story" discards it).
    SET_LAST_ANALYZED_TEXT: (state, action: PayloadAction<string | null>) => {
      state.analyzedSnapshot = action.payload;
    },
    SET_STORY_ANALYSIS: (state, action: PayloadAction<{ analysis: any[] }>) => {
      state.draft.hasStoryAnalysis = true;
      state.draft.storyAnalysis = action.payload.analysis;
    },
  },
});

export default draftSlice.reducer;
